import type { ExperienceLevel, UserDocument, UserDocumentType, VerificationStatus } from "@/domain";
import type { ComponentType } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  DocAttachmentIcon,
  DocFailedIcon,
  DocSuccessIcon,
  DocVerificationIcon
} from "./icon-utils";

// App Utility Functions

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generateRequestId(): string {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

export function extractName(fullName: string) {
  const nameParts = fullName.trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
  let middleName = "";
  if (nameParts.length > 2) {
    middleName = nameParts.slice(1, -1).join(" ");
  }
  return { firstName, middleName, lastName };
}

/** Stable icon components per status (for React static-components lint rules). */
export const verificationStatusIcons: Record<
  VerificationStatus,
  ComponentType<{ className?: string }>
> = {
  pending: DocAttachmentIcon,
  under_review: DocVerificationIcon,
  resubmitted: DocVerificationIcon,
  rejected: DocFailedIcon,
  approved: DocSuccessIcon
};

export function getVerificationStatusIcon(status: VerificationStatus) {
  return verificationStatusIcons[status];
}

// Document Management
export function getDocumentTypeTitle(documentType: UserDocumentType) {
  switch (documentType) {
    case "license":
      return "License Document";
    case "tax_id":
      return "Tax Document";
    case "certificate":
      return "Certificate";
    case "resume":
      return "Resume";
    default:
      return "Document";
  }
}

// Certificate Management
export function buildEmptyCertificateFormData() {
  return {
    certificateId: "",
    certificateNumber: "",
    creditedBy: "",
    creditedByLink: "",
    issuedDate: null,
    expiryDate: null,
    file: undefined
  };
}

// capitalize first letter
export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Truncate text to max length, appending an ellipsis (default U+2026). Total length is at most `maxLength`. */
export function truncateWithEllipsis(
  text: string,
  maxLength: number,
  ellipsis = "\u2026"
): string {
  if (text.length <= maxLength) return text;
  if (maxLength <= ellipsis.length) return ellipsis.slice(0, maxLength);
  return text.slice(0, maxLength - ellipsis.length) + ellipsis;
}

/** Format a byte count as a human-readable size (e.g. "1.5 MB"). */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const units = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + units[i];
}

/** Return a safe http/https URL from a raw string, or null if invalid. */
export function safeHttpUrl(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.href;
  } catch {
    return null;
  }
}

// Filter documents by verification document types
export function filterByVerificationDocumentTypes(documents: UserDocument[] | undefined | null) {
  return documents?.filter(doc => [
    "license",
    "tax_id",
    "certificate"
  ].includes(doc.documentType)) || [];
}

// Select values are experience-level GUIDs; the API expects integer years.
export function yearsOfExperienceFromLevelId(
  levels: ExperienceLevel[],
  selectedLevelId: string
): number | undefined {
  if (!selectedLevelId) return undefined;
  return levels.find((l) => l.id === selectedLevelId)?.fromYearsOfExperience;
}

// Delay for a given number of milliseconds
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
